import {
	ReactElement,
	useContext,
	useEffect,
	KeyboardEvent
} from "react";
import { never } from "client_legacy/ndaw/errors";
import { ActionListDivider } from "../ActionList/ActionListDivider";
import {
	CollapsibleListItemContext,
	CollapsibleListItemRenderMode,
	ItemResponsiveBehavior
} from "../CollapsibleList/CollapsibleList";
import styles from "./Toolbar.module.less";
import { Key } from "client/Misc/commonTypes";
import { useKeyboardListNavigationItem } from "client/Hooks/useKeyboardListNavigation";

export type ToolbarItemProps = {
	onExecute?: () => void;
	isGroup?: boolean;
	priority?: number;
	responsiveBehavior: ItemResponsiveBehavior | undefined;
	isDisabled?: boolean;
	renderNormalMode: (
		ref: (element: HTMLElement | null) => void,
		onKeyDown: (args: KeyboardEvent<HTMLElement>) => void,
		tabIndex: number
	) => ReactElement | null;
	renderCollapsedMode(): ReactElement | null;
};

export const ToolbarItem = (props: ToolbarItemProps): ReactElement => {
	const {
		onExecute,
		isGroup = false,
		priority = 0,
		responsiveBehavior = "collapse",
		isDisabled = false,
		renderNormalMode,
		renderCollapsedMode
	} = props;

	const {
		renderMode,
		onPriorityChange,
		resetPriority,
		onResponsiveBehaviorChange,
		resetResponsiveBehavior,
		index,
		tabIndex
	} = useContext(CollapsibleListItemContext);

	const {
		ref,
		onKeyDown
	} = useKeyboardListNavigationItem({
		itemIndex: index,
		onExecute,
		executeKey: [Key.Enter, Key.Space]
	});

	useEffect(
		(): EffectWithDestructorResult => {
			onPriorityChange(priority);
			return resetPriority;
		},
		[
			onPriorityChange,
			priority,
			resetPriority
		]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			onResponsiveBehaviorChange(responsiveBehavior);

			return resetResponsiveBehavior;
		},
		[
			onResponsiveBehaviorChange,
			responsiveBehavior,
			resetResponsiveBehavior
		]
	);

	switch (renderMode) {
		case CollapsibleListItemRenderMode.Normal:
			return (
				<div
					className={styles.itemWrapper}
				>
					{renderNormalMode(
						isDisabled ? () => null : ref,
						onKeyDown,
						tabIndex
					)}
				</div>
			);
		case CollapsibleListItemRenderMode.Collapsed:
			return (
				<>
					{isGroup && <ActionListDivider />}
					{renderCollapsedMode()}
					{isGroup && <ActionListDivider />}
				</>
			);
		default:
			return never(renderMode);
	}
};
ToolbarItem.displayName = "ToolbarItem";